import { useEffect, useRef, useState } from "react";
import { SoftphoneInstallPanel } from "./SoftphoneInstallPanel";

type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
type Metadata = {
  configured: boolean;
  version: number;
  updatedAt: string | null;
  storageReady?: boolean;
  releaseReady: boolean;
  releaseDetail?: string;
};
type Settings = {
  server: string;
  port: number;
  transport: "udp" | "tcp" | "tls";
  username: string;
  authUsername: string;
  password: string;
  realm: string;
  outboundProxy: string;
  outboundProxyPort: number | null;
  displayName: string;
  voicemailNumber: string;
};
type Revealed = {
  settings: Settings;
  version: number;
  updatedAt: string;
  hideAfterSeconds: number;
};
type Props = {
  api: Api;
  csrf?: string | undefined;
  accountId: string;
  deviceId: string;
  customer: boolean;
  readOnly?: boolean | undefined;
  deviceName: string;
};
const readableFields: [keyof Settings, string][] = [
  ["server", "SIP server / registrar"],
  ["port", "Server port"],
  ["transport", "Transport"],
  ["username", "SIP username / extension"],
  ["authUsername", "Authentication username"],
  ["password", "SIP password"],
  ["realm", "Domain / realm"],
  ["outboundProxy", "Outbound proxy"],
  ["outboundProxyPort", "Outbound proxy port"],
  ["displayName", "Display name"],
  ["voicemailNumber", "Voicemail access number"],
];

export function ManualSetupContent({
  metadata,
  revealed,
  busy,
  readOnly,
  onReveal,
  onHide,
}: {
  metadata: Metadata;
  revealed: Revealed | null;
  busy: boolean;
  readOnly: boolean;
  onReveal: () => void;
  onHide: () => void;
}) {
  return (
    <>
      {metadata.storageReady === false && (
        <p className="cr-notice error" role="alert">
          Secure storage is unavailable. Cache River must restore it before
          connection details can be opened.
        </p>
      )}
      {readOnly ? (
        <p className="cr-form-note">
          Phone credentials are hidden in customer preview. Exit preview to open
          authorized connection details.
        </p>
      ) : (
        <>
          {!metadata.configured ? (
            <div className="cr-notice" role="status">
              <h4>Connection details have not been issued</h4>
              <p>
                This device does not yet have connection details stored in the
                portal. Staff must issue its phone account before this sheet can
                show settings.
              </p>
              <p>
                You do not need to supply a SIP server, username or password.
                Those must come from Cache River, separately for each assigned
                equipment port.
              </p>
            </div>
          ) : !metadata.releaseReady ? (
            <div className="cr-notice" role="status">
              <h4>Saved details are not ready for use</h4>
              <p>
                Connection details stay locked unless the current physical
                address matches the carrier's emergency association and the
                phone server's installed emergency route matches this device.
              </p>
              <p>
                {metadata.releaseDetail ??
                  "Current device-location, emergency caller-ID and routing verification is still required before credentials can be released."}
              </p>
            </div>
          ) : null}
          {metadata.configured && !revealed && (
            <button
              type="button"
              className="cr-button"
              disabled={
                busy ||
                metadata.storageReady === false ||
                !metadata.releaseReady
              }
              onClick={onReveal}
            >
              {busy ? "Opening…" : "Show connection details"}
            </button>
          )}
          {revealed && (
            <>
              <p className="cr-form-note">
                Enter these values into your phone or ATA. This is the phone's
                login, not your portal password. Details hide after one minute
                or when you leave this tab.
              </p>
              <dl className="cr-details">
                {readableFields.map(([key, title]) => {
                  const value =
                    key === "authUsername"
                      ? revealed.settings.authUsername ||
                        revealed.settings.username
                      : revealed.settings[key];
                  if (value === "" || value === null || value === undefined)
                    return null;
                  return (
                    <div key={key}>
                      <dt>{title}</dt>
                      <dd
                        style={{ overflowWrap: "anywhere", userSelect: "text" }}
                      >
                        <code>{String(value)}</code>
                      </dd>
                    </div>
                  );
                })}
              </dl>
              <button
                type="button"
                className="cr-button secondary"
                onClick={onHide}
              >
                Hide connection details
              </button>
            </>
          )}
        </>
      )}
    </>
  );
}

export function DeviceSipPanel({
  api,
  csrf,
  accountId,
  deviceId,
  customer,
  readOnly = false,
  deviceName,
}: Props) {
  const path = `/${customer ? "customer" : "admin"}/accounts/${accountId}/equipment/${deviceId}/sip-settings`;
  const [open, setOpen] = useState(false);
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const generation = useRef(0);
  const pending = useRef<AbortController | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  function hide() {
    generation.current++;
    pending.current?.abort();
    pending.current = null;
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = null;
    setRevealed(null);
    setBusy(false);
  }
  useEffect(() => {
    hide();
    setMetadata(null);
    setError("");
    if (!open) return;
    const controller = new AbortController();
    let active = true;
    api<Metadata>(path, { signal: controller.signal })
      .then((value) => {
        if (active) setMetadata(value);
      })
      .catch((cause) => {
        if (active) setError((cause as Error).message);
      });
    return () => {
      active = false;
      controller.abort();
      hide();
    };
  }, [api, path, open, readOnly, refresh]);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") hide();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", hide);
    window.addEventListener("pagehide", hide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", hide);
      window.removeEventListener("pagehide", hide);
      hide();
    };
  }, []);
  async function reveal() {
    if (readOnly || busy || !metadata?.releaseReady) return;
    hide();
    const run = generation.current;
    const controller = new AbortController();
    pending.current = controller;
    setBusy(true);
    setError("");
    try {
      const value = await api<Revealed>(`${path}/reveal`, {
        method: "POST",
        body: {},
        csrf,
        signal: controller.signal,
      });
      if (generation.current !== run || controller.signal.aborted) return;
      setRevealed(value);
      setMetadata((old) =>
        old
          ? { ...old, version: value.version, updatedAt: value.updatedAt }
          : old,
      );
      hideTimer.current = setTimeout(
        hide,
        Math.min(60, Math.max(1, value.hideAfterSeconds)) * 1000,
      );
    } catch (cause) {
      if (generation.current === run && !controller.signal.aborted)
        setError((cause as Error).message);
    } finally {
      if (generation.current === run) setBusy(false);
    }
  }
  return (
    <details
      className="cr-panel cr-editor"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>Manual phone setup: {deviceName}</summary>
      {open && (
        <>
          <p className="cr-form-note">
            Use this instruction sheet when automatic provisioning is
            unavailable. Cache River supplies the connection details; you enter
            them into your phone or ATA.
          </p>
          {!metadata && !error && (
            <p role="status">Loading connection details…</p>
          )}
          {error && (
            <div className="cr-notice error" role="alert">
              {error}{" "}
              <button
                type="button"
                className="cr-button secondary"
                onClick={() => setRefresh((value) => value + 1)}
              >
                Reload details
              </button>
            </div>
          )}
          {metadata && (
            <ManualSetupContent
              metadata={metadata}
              revealed={revealed}
              busy={busy}
              readOnly={readOnly}
              onReveal={() => void reveal()}
              onHide={hide}
            />
          )}
          {!customer &&
            !readOnly &&
            accountId === "f59f3eb9-55bd-4dc3-bb00-3361a3abf9a0" && (
              <SoftphoneInstallPanel
                api={api}
                accountId={accountId}
                deviceId={deviceId}
                csrf={csrf}
                onInstalled={() => setRefresh((value) => value + 1)}
              />
            )}
        </>
      )}
    </details>
  );
}
